import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { HierarchyFilterComponent } from '../../../components/FilterBar/HierarchyFilter';
import { MediaTable } from '../../../components/tables/MediaTable';
import { QuarterlyCampaignsTable } from '../../../components/tables/QuarterlyCampaignsTable';
import { MachineImpactTable } from '../../../components/tables/MachineImpactTable';
import { DigitalCreativeViewer } from '../../../components/DigitalCreativeViewer';
import { ConnectedRetailVirtualSlotsTable } from '../../../components/tables/ConnectedRetailVirtualSlotsTable';
import { HierarchyFilter, RetailMediaData } from '../../../lib/types';
import { subDays } from 'date-fns';
import { formatCurrency } from '../../../lib/export';
import { CheckCircle, XCircle, ShieldCheck, ShieldAlert, TrendingUp } from 'lucide-react';

export function RetailMedia() {
  const { brandId } = useParams<{ brandId: string }>();
  const [user, setUser] = useState<any>(null);
  const [filter, setFilter] = useState<HierarchyFilter>({
    dateFrom: subDays(new Date(), 30).toISOString(),
    dateTo: new Date().toISOString(),
    granularity: 'day',
    compareToPrevious: false,
    brandScope: 'sku_or_campaign',
  });

  const [data, setData] = useState<RetailMediaData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setUser(data.user);
      });
    }
  }, []);

  useEffect(() => {
    if (user?.tenantId) {
      fetchMediaData();
    }
  }, [user, filter]);

  const fetchMediaData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        granularity: filter.granularity,
        compareToPrevious: String(filter.compareToPrevious || false),
        brandScope: filter.brandScope || 'sku_or_campaign',
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/brand/${user.tenantId}/dashboard/media?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch media data');
      }

      const data = await response.json();
      setData(data);
    } catch (error) {
      console.error('Error fetching media data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    console.log('Export CSV');
  };

  const handleExportPNG = () => {
    console.log('Export PNG');
  };

  const handleExportPagePDF = () => {
    console.log('Export Page PDF');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">Failed to load data</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Filter Section with proper separation */}
      <div className="bg-white shadow-sm">
        <HierarchyFilterComponent
          filter={filter}
          onChange={setFilter}
          onExportCSV={handleExportCSV}
          onExportPNG={handleExportPNG}
          onExportPagePDF={handleExportPagePDF}
          showBrandScope={true}
        />
      </div>

      {/* Main Content with proper spacing */}
      <div className="max-w-7xl mx-auto p-6 pt-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Retail Media Performance</h1>
          <p className="text-gray-600">Monitor media campaigns, their impact, and performance metrics</p>
        </div>

        {/* Transaction & Age Restricted Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">Total Transactions</h3>
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{data.totalTransactions.toLocaleString()}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">Successful</h3>
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-600">{data.successfulTransactions.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">
              {((data.successfulTransactions / data.totalTransactions) * 100).toFixed(1)}% success rate
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">Failed</h3>
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
            <p className="text-3xl font-bold text-red-600">{data.failedTransactions.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">
              {((data.failedTransactions / data.totalTransactions) * 100).toFixed(1)}% failure rate
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">Age Restricted Sales</h3>
              <ShieldCheck className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-purple-600">{data.ageRestrictedMetrics.totalAgeRestrictedUnitsSold.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">
              {data.ageRestrictedMetrics.totalAgeRestrictedTransactions.toLocaleString()} transactions
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">Age Verification</h3>
              <ShieldAlert className="h-5 w-5 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-orange-600">
              {((data.ageRestrictedMetrics.successfulAgeVerifications / (data.ageRestrictedMetrics.successfulAgeVerifications + data.ageRestrictedMetrics.failedAgeVerifications)) * 100).toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {data.ageRestrictedMetrics.failedAgeVerifications} failed verifications
            </p>
          </div>
        </div>

        {/* Media Performance Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Active Campaigns</h3>
            <p className="text-3xl font-bold text-gray-900">{data.mediaItems.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Total Impact</h3>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(data.mediaItems.reduce((sum, item) => sum + item.impact, 0))}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Avg Playout Time</h3>
            <p className="text-3xl font-bold text-gray-900">
              {Math.round(data.mediaItems.reduce((sum, item) => sum + item.playoutTime, 0) / data.mediaItems.length / 60)}m
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Avg Impact per Campaign</h3>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(Math.round(data.mediaItems.reduce((sum, item) => sum + item.impact, 0) / data.mediaItems.length))}
            </p>
          </div>
        </div>

        {/* Media Performance Table */}
        <div className="mb-8">
          <MediaTable data={data.mediaItems} />
        </div>

        {/* Quarterly Campaigns */}
        <div className="mb-8">
          <QuarterlyCampaignsTable data={data.quarterlyCampaigns} />
        </div>

        {/* Machine Impact Breakdown */}
        <div className="mb-8">
          <MachineImpactTable data={data.machineImpactBreakdown} />
        </div>

        {/* Digital Creative Viewer and Age Restricted Products */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <DigitalCreativeViewer creatives={data.digitalCreatives} />
          
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Age Restricted Products per Machine</h3>
            <p className="text-sm text-gray-600 mb-4">Number of age-restricted products available at each machine location</p>
            
            <div className="space-y-3 mb-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Total Age Restricted Products</p>
                <p className="text-2xl font-bold text-gray-900">{data.ageRestrictedMetrics.totalAgeRestrictedProducts}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-900 text-sm">Machine</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-900 text-sm">Products</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ageRestrictedMetrics.ageRestrictedProductsPerMachine.map((machine) => (
                    <tr key={machine.machineId} className="border-b border-gray-100">
                      <td className="py-2 px-3 text-sm text-gray-600">{machine.machineName}</td>
                      <td className="py-2 px-3 text-sm text-right font-medium text-purple-600">
                        {machine.ageRestrictedProductCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Connected Retail Virtual Slots */}
        <div className="mb-8">
          <ConnectedRetailVirtualSlotsTable data={data.connectedRetailVirtualSlots} />
        </div>

        {/* Additional Media Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Performance Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Campaign Duration</span>
                <span className="text-sm font-semibold text-gray-900">142 days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Average Campaign Length</span>
                <span className="text-sm font-semibold text-gray-900">28.4 days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">ROI Range</span>
                <span className="text-sm font-semibold text-gray-900">2.1x - 4.8x</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Media Owner Breakdown</h3>
            <div className="space-y-3">
              {Array.from(new Set(data.mediaItems.map(item => item.mediaOwner))).map((owner, index) => {
                const ownerItems = data.mediaItems.filter(item => item.mediaOwner === owner);
                const totalImpact = ownerItems.reduce((sum, item) => sum + item.impact, 0);
                return (
                  <div key={index} className="flex justify-between">
                    <span className="text-sm text-gray-600">{owner}</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(totalImpact)} ({ownerItems.length} campaigns)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
